import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { useDispatch } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';
import {
  BtnBar,
  CtaBtn,
  DangerAlert,
  FormItem,
  FormRow,
  InfoAlert,
  WaitingAlert,
  WarningAlert,
} from '@tmapy/style-guide';

import './ServiceOrder.scss';

import { OrderAttachmentsSection } from '../../app/components/OrderAttachmentsSection';
import type {
  OrderAttachmentItem,
  UploadProgressHandler,
} from '../../app/components/OrderAttachmentsSection';
import { IprDocumentItem } from '../../app/components/IprDocumentItem';
import { iprDocTypeMessages, iprDocumentMessages } from '../../app/components/iprDocumentMessages';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { QUERY_STATE } from '../../constants';
import { toBtnQueryState, useAuth, useSelector } from '../../hooks';
import { Link } from '../../link';
import { serviceOrdersAttachmentsCreate } from '../../openapi/operations/serviceOrdersAttachmentsCreate';
import { serviceOrdersAttachmentsDestroy } from '../../openapi/operations/serviceOrdersAttachmentsDestroy';
import { serviceOrdersConfirmCreate } from '../../openapi/operations/serviceOrdersConfirmCreate';
import { serviceOrdersListOperation } from '../../openapi/operations/serviceOrdersList';
import { serviceOrdersPaidCreate } from '../../openapi/operations/serviceOrdersPaidCreate';
import { serviceOrdersPartialUpdate } from '../../openapi/operations/serviceOrdersPartialUpdate';
import { useServiceOrdersRetrieveResource } from '../../openapi/operations/serviceOrdersRetrieve';
import type { PatchedServiceOrderEditRequest } from '../../openapi/types/PatchedServiceOrderEdit';
import { createHandlingFeeRows, PriceSummary } from '../../orders/components/PriceSummary';
import type { PriceSummaryRow } from '../../orders/components/PriceSummary';
import { actionResourceInvalidate } from '../../redux/resource/actions';
import type { ServerErrors } from '../../types';
import { stripEmptyFilters } from '../../utils/queryArgs';

import {
  SERVICE_ORDER_NOT_DRAFT_CODE,
  SERVICE_ORDER_NOTE_LOCKED_CODE,
  SERVICE_ORDER_PARTY_INCOMPLETE_CODE,
  SERVICE_ORDER_PRICE_CHANGED_CODE,
  SERVICE_ORDER_STATE,
} from '../constants';
import {
  formatItemQuantity,
  getCurrentPriceFromError,
  getItemName,
  getUserLabel,
  hasServerErrorCode,
} from '../utils';

import { ServiceOrderBadge } from './ServiceOrderBadge';
import type { NewServiceOrderNavigationState } from './NewServiceOrder';
import type { ServiceOrderFormHandle } from './ServiceOrderForm';
import { ServiceOrderForm } from './ServiceOrderForm';
import { ServiceOrderNoteForm } from './ServiceOrderNoteForm';
import { ServiceOrderConfirmDialog } from './ServiceOrderConfirmDialog';
import { ServiceOrderPaidDialog } from './ServiceOrderPaidDialog';
import { ServiceOrderSaveAlerts } from './ServiceOrderSaveAlerts';
import {
  serviceOrderBtnMessages,
  serviceOrderDetailMessages,
  serviceOrderFormMessages,
  serviceOrderMessages as msg,
} from './serviceOrderMessages';

type ServiceOrderDetailProps = {
  serviceOrderId: string;
};

export const ServiceOrderDetail: React.FC<ServiceOrderDetailProps> = ({ serviceOrderId }) => {
  const id = parseInt(serviceOrderId, 10);
  const dispatch = useDispatch();
  const auth = useAuth();
  const formatMessage = useMessage();
  const filters = useSelector((state) => state.filter.serviceOrderList);
  const failedAttachments = (useLocation().state as NewServiceOrderNavigationState | null)
    ?.failedAttachments;

  const resource = useServiceOrdersRetrieveResource(id ? { id } : null);
  const data = resource.data;

  const [saveQueryState, setSaveQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [saveErrors, setSaveErrors] = useState<ServerErrors | null>(null);
  const formRef = useRef<ServiceOrderFormHandle>(null);
  /** Potvrzení pracuje s uloženou verzí, neuložené úpravy by tiše propadly. */
  const [isFormEdited, setFormEdited] = useState(false);
  /** Uložení odmítnuté proto, že žádost mezitím změnila stav v jiném okně nebo u kolegyně. */
  const [staleStateMessage, setStaleStateMessage] = useState<MessageDescriptor | null>(null);
  const [confirmQueryState, setConfirmQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  /** Konflikt ceny z `confirm`; `price` může chybět, pak se vezme cena z refetche. */
  const [priceConflict, setPriceConflict] = useState<{ price: string | null } | null>(null);
  const [isPartyIncomplete, setPartyIncomplete] = useState(false);
  const [isConfirmDialogVisible, setConfirmDialogVisibility] = useState(false);
  const [paidQueryState, setPaidQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [isPaidDialogVisible, setPaidDialogVisibility] = useState(false);

  const invalidateList = useCallback(() => {
    // Přehled cachuje pod cestou včetně filtru, samotná nefiltrovaná cesta nestačí.
    const paths = new Set([
      serviceOrdersListOperation({}).path,
      serviceOrdersListOperation(stripEmptyFilters(filters)).path,
    ]);
    paths.forEach((path) => dispatch(actionResourceInvalidate(path)));
  }, [dispatch, filters]);

  const handleSave = useCallback(
    async (values: PatchedServiceOrderEditRequest) => {
      try {
        setSaveQueryState(QUERY_STATE.WAITING);
        setSaveErrors(null);
        setStaleStateMessage(null);
        const response = await serviceOrdersPartialUpdate({ query: { id }, request: values });
        if (response.ok) {
          setSaveQueryState(QUERY_STATE.SUCCESS);
          setFormEdited(false);
          setPriceConflict(null);
          setPartyIncomplete(false);
          setConfirmQueryState(QUERY_STATE.EMPTY);
          // Cena se v draftu počítá na serveru, po zápisu ji musíme načíst znovu.
          resource.refetch();
          invalidateList();
        } else {
          const payload = response.error?.data;
          const staleMessage =
            (hasServerErrorCode(payload, SERVICE_ORDER_NOT_DRAFT_CODE) &&
              serviceOrderDetailMessages.notDraft) ||
            (hasServerErrorCode(payload, SERVICE_ORDER_NOTE_LOCKED_CODE) &&
              serviceOrderDetailMessages.noteLocked) ||
            null;
          if (staleMessage) {
            setStaleStateMessage(staleMessage);
            setSaveQueryState(QUERY_STATE.EMPTY);
            resource.refetch();
            invalidateList();
          } else {
            setSaveErrors(payload ?? null);
            setSaveQueryState(QUERY_STATE.FAIL);
          }
        }
      } catch (e) {
        setSaveQueryState(QUERY_STATE.FAIL);
      }
    },
    [id, resource, invalidateList],
  );

  const handleConfirm = useCallback(async () => {
    if (!data?.total_gross) return;
    try {
      setConfirmQueryState(QUERY_STATE.WAITING);
      setPriceConflict(null);
      setPartyIncomplete(false);
      const response = await serviceOrdersConfirmCreate({
        query: { id },
        // Server porovná částku s živým přepočtem a při rozdílu potvrzení odmítne.
        request: { total_gross: data.total_gross },
      });
      if (response.ok) {
        setConfirmQueryState(QUERY_STATE.SUCCESS);
        // Jinak by formulář poznámky v potvrzené žádosti zdědil stav a chyby z ukládání draftu.
        setSaveQueryState(QUERY_STATE.EMPTY);
        setSaveErrors(null);
        setConfirmDialogVisibility(false);
        resource.refetch();
        invalidateList();
      } else {
        const payload = response.error?.data;
        setPriceConflict(
          hasServerErrorCode(payload, SERVICE_ORDER_PRICE_CHANGED_CODE)
            ? { price: getCurrentPriceFromError(payload) }
            : null,
        );
        setPartyIncomplete(hasServerErrorCode(payload, SERVICE_ORDER_PARTY_INCOMPLETE_CODE));
        setConfirmQueryState(QUERY_STATE.FAIL);
        setConfirmDialogVisibility(false);
        // Konflikt ceny znamená, že server má novější stav — ať ho pracovnice vidí.
        resource.refetch();
      }
    } catch (e) {
      setConfirmQueryState(QUERY_STATE.FAIL);
      setConfirmDialogVisibility(false);
    }
  }, [id, data?.total_gross, resource, invalidateList]);

  const handlePaid = useCallback(
    async (taxableSupplyDate: string) => {
      try {
        setPaidQueryState(QUERY_STATE.WAITING);
        const response = await serviceOrdersPaidCreate({
          query: { id },
          request: { taxable_supply_date: taxableSupplyDate },
        });
        if (response.ok) {
          setPaidQueryState(QUERY_STATE.SUCCESS);
          setPaidDialogVisibility(false);
          resource.refetch();
          invalidateList();
        } else {
          setPaidQueryState(QUERY_STATE.FAIL);
        }
      } catch (e) {
        setPaidQueryState(QUERY_STATE.FAIL);
      }
    },
    [id, resource, invalidateList],
  );

  const handlePaidDialogClose = useCallback(() => {
    setPaidDialogVisibility(false);
    setPaidQueryState(QUERY_STATE.EMPTY);
  }, []);

  const handlePaidBtnClick = useCallback(() => setPaidDialogVisibility(true), []);

  const handleConfirmBtnClick = useCallback(() => setConfirmDialogVisibility(true), []);

  const handleSaveBtnClick = useCallback(() => formRef.current?.submit(), []);

  const handleFormEdit = useCallback(() => setFormEdited(true), []);

  const handleConfirmDialogClose = useCallback(() => setConfirmDialogVisibility(false), []);

  const priceRows = useMemo(
    (): PriceSummaryRow[] => [
      ...(data?.items ?? []).map((item) => ({
        key: item.id,
        name: getItemName(item),
        vat_rate: item.vat_rate,
        price_gross: item.price_gross,
        note: (
          <>
            {item.unit_price ? (
              <FormattedMessage
                {...msg.unitPriceRow}
                values={{
                  quantity: Number(item.quantity),
                  unitPrice: Number(item.unit_price),
                }}
              />
            ) : (
              formatItemQuantity(formatMessage, item)
            )}
            {item.data_order !== null && (
              <span className='sg-a-d-b'>
                <FormattedMessage {...msg.dataOrderNumber} values={{ id: item.data_order.id }} />
              </span>
            )}
          </>
        ),
      })),
      ...createHandlingFeeRows(formatMessage, data?.handling_fee_net),
    ],
    [data, formatMessage],
  );

  const isDraft = data?.state === SERVICE_ORDER_STATE.DRAFT;
  // Potvrzení posílá částku z UI, takže nemá smysl ho nabízet, dokud ji server nespočítal.
  const canConfirm =
    isDraft && !!data?.total_gross && auth.hasPermission('ServiceOrders', 'confirm', data);
  const canMarkPaid =
    data?.state === SERVICE_ORDER_STATE.CREATED &&
    auth.hasPermission('ServiceOrders', 'paid', data);
  // Politika `ServiceOrders` stav nerozlišuje, co jde v kterém stavu měnit hlídá jen view.
  const canUpdate = auth.hasPermission('ServiceOrders', 'partial_update', data ?? {});
  const canEdit = isDraft && canUpdate;
  const canEditNote = data?.state === SERVICE_ORDER_STATE.CREATED && canUpdate;
  // Backend přílohy mění v každém stavu, zaplacenou žádost ale už FE nechává uzavřenou.
  const areAttachmentsLocked = data?.state === SERVICE_ORDER_STATE.PAID;
  const canUploadAttachment =
    !areAttachmentsLocked && auth.hasPermission('ServiceOrders', 'upload_attachment', data ?? {});
  const canDeleteAttachment =
    !areAttachmentsLocked && auth.hasPermission('ServiceOrders', 'delete_attachment', data ?? {});

  const handleAttachmentUpload = useCallback(
    async (file: File, onUploadProgress: UploadProgressHandler) => {
      const request = new FormData();
      request.set('file', file);
      const response = await serviceOrdersAttachmentsCreate({
        query: { id },
        request,
        onUploadProgress,
      });
      if (response.ok) invalidateList();
      return response;
    },
    [id, invalidateList],
  );

  const handleAttachmentDelete = useCallback(
    async (attachmentId: OrderAttachmentItem['id']) => {
      const response = await serviceOrdersAttachmentsDestroy({
        query: { id, attachment_id: `${attachmentId}` },
      });
      if (response.ok) invalidateList();
      return response;
    },
    [id, invalidateList],
  );

  return (
    <MainContent width='full'>
      <article className='sg-u-vs-4'>
        <div className='dmx-serviceOrder--head sg-u-vs-2'>
          <div className='sg-a-d-f sg-a-g-2 sg-a-ai-c sg-a-jc-sb sg-a-fw-w'>
            <header className='sg-a-d-f sg-a-ai-c sg-a-g-1 sg-a-fw-w'>
              <PageTitle>
                <FormattedMessage
                  {...serviceOrderDetailMessages.title}
                  values={{ id: data?.document_number ?? 'empty' }}
                />
              </PageTitle>
              {data && <ServiceOrderBadge state={data.state} />}
            </header>
            {(canEdit || canConfirm || canMarkPaid) && (
              <BtnBar>
                {canEdit && (
                  <CtaBtn queryState={toBtnQueryState(saveQueryState)} onClick={handleSaveBtnClick}>
                    <FormattedMessage {...serviceOrderBtnMessages.save} />
                  </CtaBtn>
                )}
                {canConfirm && (
                  <CtaBtn
                    isDisabled={confirmQueryState === QUERY_STATE.WAITING || isFormEdited}
                    tooltip={
                      isFormEdited
                        ? formatMessage(serviceOrderDetailMessages.confirmUnsaved)
                        : undefined
                    }
                    onClick={handleConfirmBtnClick}
                  >
                    <FormattedMessage {...serviceOrderBtnMessages.confirm} />
                  </CtaBtn>
                )}
                {canMarkPaid && (
                  <CtaBtn onClick={handlePaidBtnClick}>
                    <FormattedMessage {...serviceOrderBtnMessages.paid} />
                  </CtaBtn>
                )}
              </BtnBar>
            )}
          </div>

          {canEdit && (
            <ServiceOrderSaveAlerts
              queryState={saveQueryState}
              serverErrors={saveErrors}
              isSuccessVisible={!isFormEdited}
            />
          )}
          {staleStateMessage && (
            <WarningAlert>
              <FormattedMessage {...staleStateMessage} />
            </WarningAlert>
          )}
          {data && priceConflict && (
            <WarningAlert>
              <FormattedMessage
                {...serviceOrderDetailMessages.priceChanged}
                values={{ price: Number(priceConflict.price ?? data.total_gross ?? 0) }}
              />
            </WarningAlert>
          )}
          {isPartyIncomplete && (
            <DangerAlert>
              <FormattedMessage {...serviceOrderDetailMessages.partyIncomplete} />
            </DangerAlert>
          )}
          {confirmQueryState === QUERY_STATE.FAIL && !priceConflict && !isPartyIncomplete && (
            <DangerAlert>
              <FormattedMessage {...serviceOrderDetailMessages.confirmFail} />
            </DangerAlert>
          )}
        </div>

        <div className='dmx-serviceOrder--statusAlerts sg-u-vs-2'>
          {resource.queryState === QUERY_STATE.WAITING && !data && (
            <WaitingAlert>
              <FormattedMessage {...serviceOrderFormMessages.queryStateWaiting} />
            </WaitingAlert>
          )}
          {resource.queryState === QUERY_STATE.FAIL && !data && (
            <DangerAlert>
              <FormattedMessage {...serviceOrderFormMessages.loadFail} />
            </DangerAlert>
          )}

          {data && (
            <>
              {isDraft && (
                <WarningAlert>
                  <FormattedMessage {...serviceOrderDetailMessages.draftInfo} />
                </WarningAlert>
              )}
              {data.state === SERVICE_ORDER_STATE.CREATED && (
                <InfoAlert>
                  <FormattedMessage {...serviceOrderDetailMessages.confirmedInfo} />
                </InfoAlert>
              )}
              {failedAttachments && (
                <WarningAlert>
                  <FormattedMessage
                    {...serviceOrderDetailMessages.attachmentsUploadFailed}
                    values={{ names: failedAttachments.join(', ') }}
                  />
                </WarningAlert>
              )}
            </>
          )}
        </div>

        {data && (
          <div className='dmx-serviceOrder--cols'>
            <div className='sg-u-vs-4'>
              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                  <FormattedMessage {...serviceOrderDetailMessages.partiesTitle} />
                </h3>
                <div className='sg-u-box sg-a-p-2 sg-u-vs-1'>
                  <FormRow>
                    <FormItem label={formatMessage(msg.odberatel)}>
                      <Link
                        to='vydejUserDetail'
                        params={{ id: data.odberatel.id }}
                        severity='colorful'
                      >
                        {getUserLabel(data.odberatel)}
                      </Link>
                    </FormItem>
                    {data.zadatel && (
                      <FormItem label={formatMessage(msg.zadatel)}>
                        <Link
                          to='vydejUserDetail'
                          params={{ id: data.zadatel.id }}
                          severity='colorful'
                        >
                          {getUserLabel(data.zadatel)}
                        </Link>
                      </FormItem>
                    )}
                  </FormRow>
                  <FormRow>
                    <FormItem label={formatMessage(msg.creator)}>
                      {getUserLabel(data.creator)}
                    </FormItem>
                    <FormItem label={formatMessage(msg.created)}>
                      <FormattedDate value={new Date(data.created)} />{' '}
                      <FormattedTime value={new Date(data.created)} />
                    </FormItem>
                  </FormRow>
                  {(data.confirmed || data.paid) && (
                    <FormRow>
                      {data.confirmed && (
                        <FormItem label={formatMessage(msg.confirmed)}>
                          <FormattedDate value={new Date(data.confirmed)} />{' '}
                          <FormattedTime value={new Date(data.confirmed)} />
                        </FormItem>
                      )}
                      {data.paid && (
                        <FormItem label={formatMessage(msg.paid)}>
                          <FormattedDate value={new Date(data.paid)} />{' '}
                          <FormattedTime value={new Date(data.paid)} />
                        </FormItem>
                      )}
                    </FormRow>
                  )}
                  {data.taxable_supply_date && (
                    <FormRow>
                      <FormItem label={formatMessage(msg.taxableSupplyDate)}>
                        <FormattedDate value={new Date(data.taxable_supply_date)} />
                      </FormItem>
                    </FormRow>
                  )}
                </div>
              </section>

              {canEdit ? (
                <ServiceOrderForm
                  key={`${data.id}-${resource.fetchedAt}`}
                  ref={formRef}
                  data={data}
                  submitQueryState={saveQueryState}
                  serverErrors={saveErrors}
                  onSubmit={handleSave}
                  onEdit={handleFormEdit}
                />
              ) : (
                <>
                  <section>
                    <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                      <FormattedMessage {...serviceOrderFormMessages.detailsTitle} />
                    </h3>
                    <div className='sg-u-vs-2'>
                      <div className='sg-u-box sg-a-p-2 sg-u-vs-1'>
                        <FormRow>
                          <FormItem label={formatMessage(msg.name)}>{data.name}</FormItem>
                        </FormRow>
                        {!canEditNote && data.note && (
                          <FormRow>
                            <FormItem label={formatMessage(msg.note)}>
                              <span className='sg-a-ws-pw'>{data.note}</span>
                            </FormItem>
                          </FormRow>
                        )}
                      </div>
                      {canEditNote && (
                        <ServiceOrderNoteForm
                          key={`${data.id}-${resource.fetchedAt}`}
                          note={data.note}
                          submitQueryState={saveQueryState}
                          serverErrors={saveErrors}
                          onSubmit={handleSave}
                        />
                      )}
                    </div>
                  </section>
                  <section>
                    <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                      <FormattedMessage {...msg.items} />
                    </h3>
                    <div className='sg-u-box sg-a-p-2'>
                      <div className='sg-u-bt sg-u-bb sg-u-bl sg-u-br sg-u-type-sml'>
                        {data.items.map((item, index) => (
                          <div
                            key={item.id}
                            className={`sg-a-d-f sg-a-jc-sb sg-a-cg-2 sg-a-px-2 sg-a-py-1${index > 0 ? ' sg-u-bt' : ''}`}
                          >
                            <span>
                              {getItemName(item)}
                              {item.data_order !== null && (
                                <>
                                  {' · '}
                                  <Link
                                    to='vydejOrderDetail'
                                    params={{ id: item.data_order.id }}
                                    severity='colorful'
                                  >
                                    <FormattedMessage
                                      {...msg.dataOrderNumber}
                                      values={{ id: item.data_order.id }}
                                    />
                                  </Link>
                                </>
                              )}
                            </span>
                            <span className='sg-a-ws-nw sg-a-fvn-tn'>
                              {formatItemQuantity(formatMessage, item)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>
                </>
              )}
            </div>
            <div className='sg-u-vs-4'>
              <OrderAttachmentsSection
                resourceUrl={data.url}
                attachments={data.attachments}
                onUpload={canUploadAttachment ? handleAttachmentUpload : undefined}
                onDelete={canDeleteAttachment ? handleAttachmentDelete : undefined}
              />

              {data.documents.length > 0 && (
                <section>
                  <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                    <FormattedMessage {...iprDocumentMessages.sectionTitle} />
                  </h3>
                  <div className='sg-u-box sg-a-mt-1'>
                    {data.documents.map((document) => (
                      <IprDocumentItem
                        key={document.doc_type}
                        resourceUrl={data.url}
                        docType={document.doc_type}
                        label={<FormattedMessage {...iprDocTypeMessages[document.doc_type]} />}
                        note={
                          <FormattedMessage
                            {...iprDocumentMessages.documentNote}
                            values={{
                              number: document.document_number,
                              issued: new Date(document.issued),
                            }}
                          />
                        }
                      />
                    ))}
                  </div>
                </section>
              )}

              <PriceSummary
                title={<FormattedMessage {...serviceOrderDetailMessages.priceTitle} />}
                rows={priceRows}
                total_net={data.total_net}
                total_vat={data.total_vat}
                total_gross={data.total_gross}
              />
            </div>
          </div>
        )}
      </article>

      {isConfirmDialogVisible && (
        <ServiceOrderConfirmDialog
          queryState={confirmQueryState}
          onConfirm={handleConfirm}
          onClose={handleConfirmDialogClose}
        />
      )}

      {isPaidDialogVisible && (
        <ServiceOrderPaidDialog
          queryState={paidQueryState}
          onConfirm={handlePaid}
          onClose={handlePaidDialogClose}
        />
      )}
    </MainContent>
  );
};
